/**
 * Message Router - Routes inbound messages to the appropriate agent session
 *
 * Implements the routing resolution logic:
 * 1. peer matching - exact DM/group/channel ID
 * 2. guild matching - Discord server ID
 * 3. team matching - Slack team ID
 * 4. account matching - specific account instance
 * 5. channel matching - whole platform wildcard
 * 6. default - default agent
 */

import type { ConfigManager } from "../config/manager.js";
import type { ChannelConfig } from "./channels/base.js";
import type { SessionManager } from "./sessions.js";
import type { ChannelBinding, ChannelScope, InboundMessage, RouteResolution } from "./types/index.js";

/** Router options */
export interface RouterOptions {
	configManager: ConfigManager;
	sessionManager: SessionManager;
}

/** Message router */
export class MessageRouter {
	private configManager: ConfigManager;
	private sessionManager: SessionManager;

	constructor(options: RouterOptions) {
		this.configManager = options.configManager;
		this.sessionManager = options.sessionManager;
	}

	/**
	 * Resolve route for an inbound message
	 *
	 * Routing priority (high to low):
	 * 1. peer matching - exact DM/group/channel ID
	 * 2. guild matching - Discord server ID
	 * 3. team matching - Slack team ID
	 * 4. account matching - specific account instance
	 * 5. channel matching - whole platform wildcard
	 * 6. default - default agent
	 */
	async resolveRoute(message: InboundMessage): Promise<RouteResolution | null> {
		const config = this.configManager.getConfig();
		const { agentId, channel, scope, identifier } = this.parseSessionKey(message.sessionKey);

		// Get all bindings for this channel
		const bindings = config.bindings.filter((b) => b.channel === channel);

		// Priority order
		const priority: Array<ChannelBinding["bindType"]> = ["peer", "guild", "team", "account", "channel", "default"];

		for (const bindType of priority) {
			const binding = this.findBinding(bindings, bindType, {
				identifier,
				guildId: message.sender.guildId,
				teamId: message.sender.teamId,
				accountId: message.sender.accountId,
			});

			if (binding) {
				// Check access control
				if (!this.checkAccess(binding, message)) {
					continue;
				}

				const channelConfig = config.channels.find((c) => c.type === channel);
				if (!channelConfig) {
					continue;
				}

				return {
					agentId: binding.agentId,
					sessionKey: message.sessionKey,
					channelConfig,
					binding,
				};
			}
		}

		// No route found
		return null;
	}

	/**
	 * Find matching binding
	 */
	private findBinding(
		bindings: ChannelBinding[],
		bindType: ChannelBinding["bindType"],
		context: {
			identifier: string;
			guildId?: string;
			teamId?: string;
			accountId?: string;
		},
	): ChannelBinding | undefined {
		return bindings.find((b) => {
			if (b.bindType !== bindType) return false;

			switch (bindType) {
				case "peer":
					// Match exact DM/group/channel ID
					return b.bindValue === context.identifier;

				case "guild":
					// Match Discord server ID
					return b.bindValue === context.guildId;

				case "team":
					// Match Slack team ID
					return b.bindValue === context.teamId;

				case "account":
					// Match specific account
					return b.bindValue === context.accountId;

				case "channel":
				case "default":
					// Wildcard match
					return b.bindValue === "*";

				default:
					return false;
			}
		});
	}

	/**
	 * Check if sender has access based on binding policy
	 */
	private checkAccess(binding: ChannelBinding, message: InboundMessage): boolean {
		const { scope } = this.parseSessionKey(message.sessionKey);
		const sender = message.sender;
		const access = binding.access;

		if (scope === "dm") {
			// DM access control
			switch (access.dmPolicy) {
				case "open":
					return true;
				case "allowlist":
					return access.dmAllowlist?.includes(sender.id) ?? false;
				case "pairing":
				default:
					// Pairing requires explicit approval (handled elsewhere)
					return true;
			}
		} else {
			// Group access control
			switch (access.groupPolicy) {
				case "open":
					return true;
				case "allowlist":
					return access.groupAllowlist?.includes(sender.id) ?? false;
				case "mention":
				default:
					// In groups, require mention (handled by channel adapter)
					return true;
			}
		}
	}

	/**
	 * Parse session key into components
	 */
	parseSessionKey(key: string): {
		agentId: string;
		channel: string;
		scope: ChannelScope;
		identifier: string;
	} {
		const parts = key.split(":");
		return {
			agentId: parts[1] ?? "default",
			channel: parts[2] ?? "unknown",
			scope: (parts[3] as ChannelScope) ?? "dm",
			identifier: parts[4] ?? "unknown",
		};
	}

	/**
	 * Build session key from components
	 */
	buildSessionKey(agentId: string, channel: string, scope: ChannelScope, identifier: string): string {
		return `agent:${agentId}:${channel}:${scope}:${identifier}`;
	}

	/**
	 * Check if message should be processed (access control)
	 */
	async canProcess(message: InboundMessage): Promise<{
		allowed: boolean;
		reason?: string;
		requiresPairing?: boolean;
	}> {
		const route = await this.resolveRoute(message);

		if (!route) {
			return {
				allowed: false,
				reason: "No agent configured for this channel/identifier",
			};
		}

		const { scope } = this.parseSessionKey(message.sessionKey);
		const sender = message.sender;
		const { binding } = route;

		// Check DM policy
		if (scope === "dm" && binding.access.dmPolicy === "pairing") {
			// TODO: Check if sender is paired
			// For now, allow all
		}

		// Check group policy
		if (scope !== "dm" && binding.access.groupPolicy === "mention") {
			// Channel adapter should handle mention detection
			// If message reaches here, it was either a mention or direct message
		}

		return { allowed: true };
	}

	/**
	 * Get or create session for message
	 */
	async getOrCreateSession(message: InboundMessage) {
		const { agentId, channel, scope, identifier } = this.parseSessionKey(message.sessionKey);

		return this.sessionManager.getOrCreateSession(agentId, channel, scope, identifier);
	}

	/**
	 * Get agent configuration for message
	 */
	getAgentConfig(message: InboundMessage) {
		const { agentId } = this.parseSessionKey(message.sessionKey);
		return this.configManager.getAgent(agentId);
	}

	/**
	 * Get all active routes
	 */
	getActiveRoutes(): Array<{
		channel: string;
		agentId: string;
		bindType: string;
		bindValue: string;
	}> {
		const config = this.configManager.getConfig();
		return config.bindings.map((b) => ({
			channel: b.channel,
			agentId: b.agentId,
			bindType: b.bindType,
			bindValue: b.bindValue,
		}));
	}
}
